import React from 'react';

import {GenericPaginatedList} from 'app/components/general/lists/GenericPaginatedList';
import {ItemWithChildrenStructure} from 'app/general/structures/ItemWithChildrenStructure';
import {StoredRoleFetcher} from 'app/roles/datafetchers';
import {useAccountSettings} from 'app/settings/api';
import {StoredUsersFetcher} from 'app/user/datafetchers';
import {gt} from 'app/utils/i18n';

import {AddNewTeamRoleButton} from './AddNewTeamRoleButton';
import {
  DenormalizedRoleSerializedType,
  TeamRolesListItem,
} from './TeamRolesListItem';
import {TeamRolesUserListItem} from './TeamRolesUserListItem';

import type {actionsGetterType} from 'app/general/widgets/ThreeDotsDropdownMenu';
import type {TeamSerializedType, UserSerializedType} from 'sweet-types';

interface Props {
  team: TeamSerializedType;
  memberActionsGetter: actionsGetterType<UserSerializedType>;
}

export const TeamRolesList = ({team, memberActionsGetter}: Props) => {
  const {data: settings} = useAccountSettings(undefined, {
    refetchOnMountOrArgChange: 900,
  });
  return (
    <>
      <GenericPaginatedList<
        DenormalizedRoleSerializedType,
        any,
        {
          team: TeamSerializedType;
          memberActionsGetter: actionsGetterType<UserSerializedType>;
        },
        Record<string, never>
      >
        ItemComponent={TeamRolesListItem}
        FetcherComponent={StoredRoleFetcher}
        paramsBuilder={() => ({
          team_id: team.id,
        })}
        pageParamName="roles_page"
        customProps={() => ({memberActionsGetter, team})}
        noItemsMessage={(perms, NoResultsMessage) => (
          <NoResultsMessage
            classNameOverwrite="mt-4 text-center"
            head={gt('This team has no roles yet')}
          >
            <>
              <p>
                {gt(
                  'Roles allows you to organize team members and assign tasks to all members in a role.'
                )}
              </p>
              {team.can_edit ? (
                <div>
                  <AddNewTeamRoleButton team={team} placement="bottom">
                    {(handlers) => (
                      <button className="btn btn-outline-primary" {...handlers}>
                        {gt('Create a role for this team')}
                      </button>
                    )}
                  </AddNewTeamRoleButton>
                </div>
              ) : null}
            </>
          </NoResultsMessage>
        )}
      />
      <GenericPaginatedList<
        UserSerializedType,
        any,
        {
          team: TeamSerializedType;
          actionsGetter: actionsGetterType<UserSerializedType>;
          canEdit: boolean;
          accountOwnerEmail?: string;
        },
        Record<string, never>
      >
        ItemComponent={TeamRolesUserListItem}
        FetcherComponent={StoredUsersFetcher}
        paramsBuilder={() => ({
          team_id: team.id,
          in_no_team_roles: team.id,
        })}
        noItemsMessage={() => null}
        pageParamName="users_page"
        customProps={() => ({
          actionsGetter: memberActionsGetter,
          team,
          canEdit: team.can_edit,
          accountOwnerEmail: settings?.owner_email,
        })}
      >
        {(response, fetchData, list) =>
          !response.loading && response.count > 0 ? (
            <ItemWithChildrenStructure
              expandedChildren={list}
              containerClass="mb-3"
              childrenClass="border-bottom"
            >
              <div className="p-2 d-flex align-items-center flex-grow-1 text-secondary font-italic">
                {gt('Team members without a role')}
              </div>
            </ItemWithChildrenStructure>
          ) : null
        }
      </GenericPaginatedList>
    </>
  );
};
